import { useEffect, useState } from 'react';
import { useAuth, API, socket } from '../App';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Send, User } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import VideoCallButton from '../components/VideoCallButton';

export default function Messages() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOriginal, setShowOriginal] = useState({}); // Track per message

  useEffect(() => {
    fetchConversations();
    
    // Get user from URL params if exists
    const params = new URLSearchParams(location.search);
    const userId = params.get('user');
    if (userId) {
      fetchUserAndStartConversation(userId);
    }

    // Listen for new messages
    if (socket) {
      socket.on('new_message', (message) => {
        if (selectedConversation && 
            (message.sender_id === selectedConversation.id || message.receiver_id === selectedConversation.id)) {
          // Use translated content if available
          const displayMessage = {
            ...message,
            content: message.translated_content || message.content
          };
          setMessages(prev => [...prev, displayMessage]);
        }
        fetchConversations();
        toast.info(t('messages.newMessage'));
      });
    }

    return () => {
      if (socket) {
        socket.off('new_message');
      }
    };
  }, [location, selectedConversation, socket]);

  const fetchUserAndStartConversation = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}`, { withCredentials: true });
      setSelectedConversation(response.data);
      fetchMessages(userId);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, { withCredentials: true });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const response = await axios.get(`${API}/messages/${otherUserId}`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error(t('errors.generic'));
    }
  };

  const handleSelectConversation = (conversationUser) => {
    setSelectedConversation(conversationUser);
    fetchMessages(conversationUser.id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        receiver_id: selectedConversation.id,
        content: newMessage
      }, { withCredentials: true });

      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const toggleShowOriginal = (messageId) => {
    setShowOriginal(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const getMessageContent = (message) => {
    // If showing original or no translation exists
    if (showOriginal[message.id] || !message.translated_content) {
      return message.original_content || message.content;
    }
    // Show translation
    return message.translated_content || message.content;
  };

  const isTranslated = (message) => {
    return message.translated_content && message.sender_lang && message.sender_lang !== user?.preferred_language;
  };

  return (
    <Layout>
      <div data-testid="messages-page" className="h-[calc(100vh-12rem)]">
        <Card className="h-full flex overflow-hidden">
          {/* Conversations List */}
          <div className="w-80 border-r flex flex-col">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">Messages</h2>
            </div>
            <ScrollArea className="flex-1">
              {conversations.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p>{t('messages.noConversations')}</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <button
                    key={conv.user.id}
                    data-testid={`conversation-${conv.user.id}`}
                    onClick={() => handleSelectConversation(conv.user)}
                    className={`w-full p-4 flex items-start gap-3 hover:bg-gray-50 transition-colors border-b ${
                      selectedConversation?.id === conv.user.id ? 'bg-teal-50' : ''
                    }`}
                  >
                    <Avatar>
                      <AvatarImage src={conv.user.picture} />
                      <AvatarFallback className="bg-teal-100 text-teal-600">
                        {conv.user.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900">{conv.user.name}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {conv.last_message.content}
                      </p>
                    </div>
                    {!conv.last_message.read && conv.last_message.receiver_id === user?.id && (
                      <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                    )}
                  </button>
                ))
              )}
            </ScrollArea>
          </div>

          {/* Messages Area */}
          <div className="flex-1 flex flex-col">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={selectedConversation.picture} />
                      <AvatarFallback className="bg-teal-100 text-teal-600">
                        {selectedConversation.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-gray-900">{selectedConversation.name}</p>
                      <p className="text-sm text-gray-500">@{selectedConversation.username || selectedConversation.email.split('@')[0]}</p>
                    </div>
                  </div>
                  <VideoCallButton otherUserId={selectedConversation.id} otherUserName={selectedConversation.name} />
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((message) => {
                      const isMine = message.sender_id === user?.id;
                      const translated = isTranslated(message);
                      return (
                        <div
                          key={message.id}
                          data-testid={`message-${message.id}`}
                          className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}
                        >
                          {translated && !isMine && (
                            <div className="flex items-center gap-2 mb-1 px-2">
                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-600">
                                🌍 Auto-translated {message.sender_lang?.toUpperCase()} → {user?.preferred_language?.toUpperCase()}
                              </Badge>
                              <button
                                onClick={() => toggleShowOriginal(message.id)}
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                {showOriginal[message.id] ? 'Show Translation' : 'Show Original'}
                              </button>
                            </div>
                          )}
                          <div
                            className={`max-w-md px-4 py-2 rounded-2xl ${
                              isMine
                                ? 'bg-teal-500 text-white rounded-br-none'
                                : 'bg-gray-100 text-gray-900 rounded-bl-none'
                            }`}
                          >
                            <p>{getMessageContent(message)}</p>
                            <p
                              className={`text-xs mt-1 ${
                                isMine ? 'text-teal-100' : 'text-gray-500'
                              }`}
                            >
                              {new Date(message.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      data-testid="message-input"
                      type="text"
                      placeholder={t('messages.typePlaceholder')}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="flex-1"
                      disabled={loading}
                    />
                    <Button
                      data-testid="send-message-button"
                      type="submit"
                      disabled={loading || !newMessage.trim()}
                      className="bg-teal-500 hover:bg-teal-600 text-white px-6 rounded-lg"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>{t('messages.selectConversation')}</p>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}